import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Manifest, ManifestFilters, ManifestStatus } from '../models';
import { useManifests } from './useManifests';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCourierIntegrations } from '../../List/hooks/useCourierIntegrations';

type SortingState = { field: string; direction: 'asc' | 'desc' } | null;

const INITIAL_SELECTED_COLUMNS = [
  'manifest_number',
  'status',
  'courier_integration_id',
  'orders_count',
  'packages_count',
  'weight_total',
  'created_at',
  'completed_at',
  'actions',
] as const;

const FIXED_COLUMNS = ['manifest_number', 'status'] as const;

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value as FilterConditionOutput[];
};

export const useManifestsMain = () => {
  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    search: { type: 'string', default: '' as string },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'created_at' as string },
    sortDirection: { type: 'string', default: 'desc' as string },
  });

  const { data: courierIntegrations = [] } = useCourierIntegrations();

  const initialConditions = parseFiltersFromParams(queryParams.filters);

  const [searchTerm, setSearchTerm] = useState<string>((queryParams.search ?? '').trim());
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(initialConditions);
  const filterConditionsSerializedRef = useRef<string>(serializeConditions(initialConditions));
  const [sorting, setSorting] = useState<SortingState>(() => {
    const sortField = queryParams.sortBy || 'created_at';
    const direction = queryParams.sortDirection === 'asc' ? 'asc' : 'desc';
    return {
      field: sortField,
      direction,
    };
  });
  const [selectedColumns, setSelectedColumns] = useState<string[]>([...INITIAL_SELECTED_COLUMNS]);
  const [cachedManifests, setCachedManifests] = useState<Manifest[]>([]);
  const [cachedTotal, setCachedTotal] = useState(0);

  useEffect(() => {
    const paramSearch = (queryParams.search ?? '').trim();
    setSearchTerm((prev) => (prev === paramSearch ? prev : paramSearch));
  }, [queryParams.search]);

  useEffect(() => {
    const nextConditions = parseFiltersFromParams(queryParams.filters);
    const nextSerialized = serializeConditions(nextConditions);

    if (nextSerialized !== filterConditionsSerializedRef.current) {
      filterConditionsSerializedRef.current = nextSerialized;
      setFilterConditions(nextConditions);
    }
  }, [queryParams.filters]);

  useEffect(() => {
    const sortField = queryParams.sortBy || 'created_at';
    const direction = queryParams.sortDirection === 'asc' ? 'asc' : 'desc';

    setSorting((prev) => {
      if (prev && prev.field === sortField && prev.direction === direction) {
        return prev;
      }

      return {
        field: sortField,
        direction,
      };
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  const queryFilters = useMemo(() => {
    const baseFilters: ManifestFilters = {
      per_page: 25,
    };

    if (searchTerm) {
      baseFilters.manifest_number = searchTerm;
    }

    filterConditions.forEach((condition) => {
      if (condition.column === 'status' && condition.action === 'equals') {
        baseFilters.status = condition.value as ManifestStatus;
      }
      if (condition.column === 'courier_integration_id' && condition.action === 'equals') {
        baseFilters.courier_integration_id = Number(condition.value);
      }
      if (condition.column === 'created_at' && condition.action === 'equals') {
        if (typeof condition.value === 'string' && condition.value.includes(' to ')) {
          const [from, to] = condition.value.split(' to ');
          baseFilters.created_from = from;
          baseFilters.created_to = to;
        }
      }
    });

    if (sorting?.field && sorting?.direction) {
      baseFilters.sorting = {
        column: sorting.field,
        direction: sorting.direction,
      };
    }

    return baseFilters;
  }, [searchTerm, filterConditions, sorting]);

  const {
    manifests: fetchedManifests,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total: fetchedTotal,
  } = useManifests(queryFilters);

  useEffect(() => {
    if (fetchedManifests.length > 0) {
      setCachedManifests(fetchedManifests);
      setCachedTotal(fetchedTotal);
    }
  }, [fetchedManifests.length, fetchedTotal]);

  const handleSearch = useCallback(
    (term: string) => {
      const trimmedTerm = term.trim();
      setSearchTerm(trimmedTerm);
      updateParam('search', trimmedTerm);
    },
    [updateParam],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      const serialized = serializeConditions(conditions);

      if (filterConditionsSerializedRef.current === serialized) {
        return;
      }

      filterConditionsSerializedRef.current = serialized;
      setFilterConditions(conditions);
      updateParam('filters', conditions);
    },
    [updateParam],
  );

  const handleColumnsChange = useCallback((columns: string[]) => {
    setSelectedColumns(columns);
  }, []);

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setSorting(newSorting);
      if (newSorting && newSorting.field) {
        updateParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      } else {
        updateParams({
          sortBy: 'created_at',
          sortDirection: 'desc',
        });
      }
    },
    [updateParams],
  );

  const hasActiveFilters = useMemo(
    () => filterConditions.length > 0 || Boolean(searchTerm.trim()),
    [filterConditions.length, searchTerm],
  );

  const shouldUseCachedData = !hasActiveFilters && fetchedManifests.length === 0 && cachedTotal > 0;

  const manifests = shouldUseCachedData ? cachedManifests : fetchedManifests;
  const total = shouldUseCachedData ? cachedTotal : fetchedTotal;

  const manifestsViewProps = useMemo(
    () => ({
      manifests,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
    }),
    [manifests, fetchNextPage, hasNextPage, isFetchingNextPage, total],
  );

  return {
    manifests,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    sorting,
    selectedColumns,
    searchTerm,
    manifestsViewProps,
    hasActiveFilters,
    fixedColumns: [...FIXED_COLUMNS],
    filterConditions,
    courierIntegrations,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
  };
};
